import { debugLog } from '@/lib/debugLog';
import { newClientRequestId, recordApiCall } from '@/lib/diagnostics/diagnosticsBuffer';
import { ApiHttpError, type ApiError, type BaseResponse } from '@/types';

/**
 * HTTP api client 的設定。
 *
 * @property baseUrl  後端網址 prefix；空字串表示「相對路徑」，配合 Vite dev proxy 使用。
 * @property fetchImpl  測試時可注入假 fetch；正式環境留空使用 ``globalThis.fetch``。
 * @property onAuthLost  401 + ``AUTH_INVALID_TOKEN`` 觸發的 callback。**R45 起無 refresh
 *                       chain**（spec §1.4 — `POST /api/front_auth/refresh` 410 Gone），
 *                       401 直接 callback。清 zustand store / navigate ``/login`` 由
 *                       ``services/index.ts`` 注入的 handler 處理（避免 httpApiClient
 *                       反向依賴 store / router 層）。
 * @property getCurrentGroupId  R45 起每個非 exempt path 自動帶 ``X-Current-Group`` header
 *                       （spec §2.3）。此 getter 由 ``services/index.ts`` inject；回
 *                       ``null`` 時 skip 注入（auth bootstrap 流程中尚未拿到 gid 是合法
 *                       狀態，BE 自然回 400 ``CURRENT_GROUP_REQUIRED``）。
 *
 * @remarks
 * Round 12 起對齊新 spec，認證一律走 HttpOnly cookie（``credentials: 'include'``）；
 * **不再**從 store 取 access token 加 ``Authorization: Bearer`` header。``LoginResult.accessToken``
 * 在前端只是 informational state，UI 顯示 / debug 用，不參與 HTTP 認證。
 */
export interface HttpClientConfig {
  baseUrl: string;
  fetchImpl?: typeof fetch;
  onAuthLost?: () => void;
  getCurrentGroupId?: () => string | null;
}

/**
 * 把相對路徑轉成完整 URL。
 *
 * - 已是 ``http(s)://`` 開頭：原樣回傳。
 * - ``baseUrl`` 為空字串：保持相對路徑（dev 時走 Vite proxy）。
 * - 其餘：``${baseUrl}${path}``。
 */
export function buildApiUrl(path: string, baseUrl: string): string {
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  if (!baseUrl) return path;
  return `${baseUrl}${path}`;
}

/**
 * 從 ``document.cookie`` 讀 ``csrf_token`` cookie 值（後端登入 / refresh 時下發的非 HttpOnly cookie）。
 * 拿不到就回空字串 — 呼叫端自行決定要不要送 ``X-CSRF-Token`` header。
 */
export function getCsrfToken(): string {
  if (typeof document === 'undefined') return '';
  const m = /(?:^|;\s*)csrf_token=([^;]+)/.exec(document.cookie);
  return m ? decodeURIComponent(m[1] ?? '') : '';
}

const READ_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

/**
 * Auth-entry 端點（不需要 / 不該帶 CSRF；後端有 exempt）：
 *   - ``/google_login`` / ``/login``：使用者尚未有 session，沒 csrf_token cookie 可送
 *   - ``/refresh``：**R45 起 410 Gone**（spec §1.4 砍 refresh chain），但仍在 exempt
 *     清單裡防呆 — 萬一有殘留 caller 打過去，不該因 CSRF 而 403 而是 410
 */
export const CSRF_EXEMPT_SUFFIXES = [
  '/api/front_auth/google_login',
  '/api/front_auth/login',
  '/api/front_auth/refresh',
] as const;

function isCsrfExempt(path: string): boolean {
  return CSRF_EXEMPT_SUFFIXES.some((suffix) => path.endsWith(suffix));
}

/**
 * ``X-Current-Group`` header exempt 路徑（spec §2.3 + R45 8-agent panel synthesis）：
 *   - ``/api/front_auth/*``：auth entry，session 還沒建立沒 group context
 *   - ``/api/auth/firebase-token``：取 Firebase custom token（不掛 L1.5）
 *   - ``/api/me``：取個人 profile（BE 從 profile.current_group_id 反查 wallet）。
 *     ⚠️ **這條在邏輯上必須豁免** — bootstrap 第一步就是 `GET /me` 去拿
 *     `current_group_id`,那時還不知道要帶什麼。BE 端手寫三段防護,永不 400
 *     (最差回 `current_group_id=""` + `wallet=null`)。
 *   - ``/api/groups`` + ``/api/groups/*``：group endpoint 本身是 group context source
 *   - ~~``/api/ads/*``~~：**R171 移除此豁免**。R45 當初是 P0 defensive default(spec §2.3
 *     沒列 ads,FE 先 opt-out 防破 e2e),註解寫「BE 確認後改 1 行」——
 *     creative-test-v2 docs/80 對帳就是那個確認:ads 屬 L1.5「有 fallback」類,**沒帶
 *     header 後端會靜默採用 `profile.current_group_id`**。目前只因為「切換工作區時會
 *     PATCH /me」才碰巧一致 —— 哪天切換不再寫 /me,ads 的寫入就會落到錯的 group。
 *     顯式帶上 header 消掉這個隱性相依(BE 明確建議)。
 *   - ``/api/projects/{pid}/jobs``、``/jobs/{jid}``、``/jobs/{jid}/download-url`` 等
 *     legacy job 子路徑:**R48.x 對齊 BE handoff 2026-05-25** —「legacy
 *     `_LEGACY_JOB_DEPS`,前一 commit 拿掉 L1.5」。submit / continue / cancel /
 *     download-url 都不掛 L1.5,FE 端 skip 注入避免 BE 端誤判 / leak gid。
 *
 * **Round 50 — pattern 收斂**:把單一寬鬆 `^/api/projects/{pid}/jobs(/...)` regex
 * 拆成 4 條 explicit suffix match,避免未來新加 job sub-resource 路徑(eg.
 * `/jobs/{jid}/metrics`)被自動 exempt 掉。Security agent audit finding —
 * `/assets/{aid}/download-url` 走 L1.5-exempt 可能讓 cross-group user 拿著別人
 * project asset 的 download-url URL 直接打,等 BE owner 確認(見 CHANGELOG)。
 */
const GROUP_HEADER_EXEMPT_PATTERNS: ReadonlyArray<RegExp> = [
  /^\/api\/front_auth(\/|$|\?)/,
  /^\/api\/auth(\/|$|\?)/,
  /^\/api\/me(\?|$)/,
  /^\/api\/groups(\/|\?|$)/,
  // T5(docs/50)— 說明知識庫(M-18)非 group-scoped(登入即可),不掛 X-Current-Group
  /^\/api\/help(\/|$|\?)/,
  // P3 — workflow catalog 是全域定價/schema 表(非 project/group scope),不掛 X-Current-Group
  /^\/api\/workflow(\/|$|\?)/,
  // POST /api/projects/{pid}/jobs 或 GET list
  /^\/api\/projects\/[^/]+\/jobs(?:\?|$)/,
  // GET /api/projects/{pid}/jobs/{jid}
  /^\/api\/projects\/[^/]+\/jobs\/[^/]+(?:\?|$)/,
  // POST /api/projects/{pid}/jobs/{jid}/continue 或 /cancel
  /^\/api\/projects\/[^/]+\/jobs\/[^/]+\/(?:continue|cancel)(?:\?|$)/,
  // GET /api/projects/{pid}/jobs/{jid}/assets/{aid}/download-url
  /^\/api\/projects\/[^/]+\/jobs\/[^/]+\/assets\/[^/]+\/download-url(?:\?|$)/,
];

function isGroupHeaderExempt(path: string): boolean {
  return GROUP_HEADER_EXEMPT_PATTERNS.some((re) => re.test(path));
}

/**
 * 把 wire 端 `envelope.error` 物件 normalize 成 domain :type:`ApiError`。
 *
 * 後端 BaseResponse envelope 的 `error` 是 **wire snake shape**
 * `{ code, message, reason_code?, limit? }`,但 domain `ApiError` 用 **camel**
 * `reasonCode`(types/api.ts);若直接把 wire 原物當 `ApiError`,`err.error.reasonCode`
 * 永遠 undefined,billing 取消競態判定(`errorMap.getCancelReasonCode`)全 miss。此
 * helper 在 transport 邊界統一補映射(後端 Q5 確認 `reason_code` 在 envelope
 * `error.reason_code`)。
 *
 * `limit`(只 `WORKSPACE_QUOTA_EXCEEDED` 帶;docs/47 §0.1/§3.E)wire/domain 同名,
 * 僅在缺值且 runtime 帶 number 時補上(供工作區數上限文案免 hardcode)。
 *
 * 純加法、不丟失 `code`(number 或 string 都原樣保留):僅在對應 camel 欄位尚未帶值
 * 且 runtime 帶合法型別時補上;否則原樣回。`raw` 為 null / 缺 `code` 時回 null,讓
 * 呼叫端 fallback chain 繼續。
 */
export function normalizeEnvelopeError(raw: ApiError | null | undefined): ApiError | null {
  if (raw == null || raw.code == null) return null;
  let next = raw;
  // reason_code(snake)→ reasonCode(camel);已帶 camel 則略過。
  if (next.reasonCode === undefined) {
    const snake = (next as { reason_code?: unknown }).reason_code;
    if (typeof snake === 'string') next = { ...next, reasonCode: snake };
  }
  // limit:wire/domain 同名,缺值且為 number 才補(避免帶進髒型別)。
  if (next.limit === undefined) {
    const lim = (next as { limit?: unknown }).limit;
    if (typeof lim === 'number') next = { ...next, limit: lim };
  }
  return next;
}

/**
 * 從 FastAPI `{detail:{code,message,reason_code?}}` **物件**錯誤殼抽出 :type:`ApiError`。
 *
 * 後端 Q5 確認:billing 等新 endpoint 的真實錯誤殼是 BaseResponse envelope
 * `{ success, error:{ code, message, reason_code } }`,**不是** docs/46 §2.2 寫的
 * `{detail:{...}}`;此 detail-object handler 對 billing envelope **永不觸發**,
 * 留作非 billing endpoint(若有 FastAPI 直拋物件形 `detail`)的防禦 fallback。
 *
 * 純加法:`detail` 是陣列(Pydantic)/ 字串 / 缺 `code` 時回 null,呼叫端維持
 * 既有 fallback 路徑不變。
 */
export function extractDetailObjectError(parsed: unknown): ApiError | null {
  if (typeof parsed !== 'object' || parsed === null) return null;
  const detail = (parsed as { detail?: unknown }).detail;
  if (typeof detail !== 'object' || detail === null || Array.isArray(detail)) return null;
  const d = detail as { code?: unknown; message?: unknown; reason_code?: unknown };
  if (typeof d.code !== 'string' || d.code.length === 0) return null;
  const error: ApiError = {
    code: d.code,
    message: typeof d.message === 'string' ? d.message : d.code,
  };
  if (typeof d.reason_code === 'string') error.reasonCode = d.reason_code;
  return error;
}

/** `httpRequestWithRefresh` 的內部選項。 */
interface InternalRequestOptions {
  /**
   * 額外 header — 例如 Phase 3 Job submit / continue 帶的 ``X-Idempotency-Key``。
   * 注意：``Content-Type``、``X-CSRF-Token`` 跟 ``X-Current-Group`` 由 :func:`httpRequest`
   * 自動處理；caller 給的同名 key **不會**被覆蓋（後設定優先），但建議避開以免行為不直觀。
   */
  extraHeaders?: Record<string, string>;
}

/**
 * 對後端發出一次請求，回傳完整 :type:`BaseResponse` 外殼。
 *
 * 處理流程：
 * 1. 自動帶 ``credentials: 'include'`` 讓瀏覽器送 cookie（Authorization / Refresh-Token / csrf_token）。
 * 2. 寫操作（POST/PUT/PATCH/DELETE）且非 auth-entry 端點：自動加 ``X-CSRF-Token`` header。
 * 3. 422 走 FastAPI 的 ``{detail, body}`` 格式，包成 :class:`ApiHttpError`。
 * 4. 其餘非 2xx 或 ``success=false``：解析 body 的 ``error`` 欄位再丟例外。
 *
 * **不**做 401 自動 refresh — 那是 :func:`httpRequestWithRefresh` 的職責，避免 auth-entry
 * 端點（``signIn`` / ``refresh`` 自己）誤觸無限遞迴。
 */
async function httpRequestCore<T>(
  config: HttpClientConfig,
  method: string,
  path: string,
  body: unknown,
  extraHeaders: Record<string, string> | undefined,
  clientRequestId: string,
  trace: { status: number | null },
): Promise<BaseResponse<T>> {
  const fetchImpl = config.fetchImpl ?? globalThis.fetch;
  const url = buildApiUrl(path, config.baseUrl);

  // R245 — multipart 分支(封面圖上傳 `POST /projects|brand_kits/{id}/cover`)。
  //
  // ⚠️ FormData **絕對不能自己設 `Content-Type`**:multipart 的 header 值長這樣
  //   `multipart/form-data; boundary=----WebKitFormBoundaryXXXX`
  // 那個 boundary 是 fetch 送出時才生成的,只有瀏覽器知道。手動蓋成
  // `application/json`(或就算蓋成 `multipart/form-data` 但沒 boundary)後端一律
  // 解不出欄位 → 422。所以這裡是「刻意不設」而不是忘了設。
  //
  // 其餘一切照舊:CSRF / X-Current-Group / credentials / 401 chain / 錯誤解析都
  // 與 JSON 請求走同一條路 —— 封面上傳是寫操作,CSRF 一樣要帶。
  const isMultipart = typeof FormData !== 'undefined' && body instanceof FormData;

  const headers: Record<string, string> = {};
  if (body !== undefined && !isMultipart) headers['Content-Type'] = 'application/json';
  // R249 — 回報系統的關聯鍵。每個請求都帶,BE 的 Logfire span 記下同一個值之後,一份
  // 回報單裡的 id 清單就能在 Logfire 直接撈出該使用者當下完整的 server trace。
  //
  // ⚠️ **BE 還沒讀這個 header**(Step 3 才接)—— 現在就送是刻意的:①未知 header 對
  // FastAPI 是 no-op,零風險 ②same-origin proxy 不觸發 CORS preflight,所以不受 BE
  // `allow_headers` 白名單影響(BE 接的那天仍要補進去,那是 BE 自己的紅線)③提早送才能
  // 及早發現 header 被 infra 擋掉(Cloud Armor 對 multipart 的誤判就是這樣才浮出來的;
  // 值是純 Crockford base32,不含 CR/LF,不會踩 921150)。
  headers['X-Client-Request-Id'] = clientRequestId;
  if (!READ_METHODS.has(method.toUpperCase()) && !isCsrfExempt(path)) {
    const csrf = getCsrfToken();
    if (csrf) {
      headers['X-CSRF-Token'] = csrf;
      debugLog('csrf', `${method} ${path} token=${csrf.slice(0, 8)}…`);
    } else {
      debugLog('csrf', `${method} ${path} ⚠️ csrf_token cookie not found`);
    }
  }
  // R45 起每個非 exempt path 自動帶 X-Current-Group（spec §2.3 + R45 8-agent panel）。
  // 讀寫操作都需要:spec §1.5 範例 wrapper 對所有方法注入;只在 path exempt 或 gid
  // 尚未拿到時 skip。
  //
  // R171 更正(creative-test-v2 docs/80 對帳):OpenAPI 上 67 條**宣告**了這個 header,
  // 但 `required=True` 的是 **0 條** —— 所以「沒帶就 400」是舊的錯誤理解。實況三類:
  //   ① L1.5 有 fallback(ads / brand_kits / uploads intent / assets / dashboard…):
  //      沒帶 → 退回 `profile.current_group_id`,兩者皆空才 400。
  //   ② 無 L1.5(`/projects/*/jobs*`、`/projects/*/assets*`):不需要;⚠️ **送了且
  //      group 不符反而 400 `GROUP_PROJECT_MISMATCH`** → 這類維持豁免。
  //   ③ `GET /me`:永不 400。
  // 結論:能帶就帶(消掉①的靜默 fallback 風險),②③ 刻意不帶。
  if (!isGroupHeaderExempt(path)) {
    const gid = config.getCurrentGroupId?.();
    if (gid) {
      headers['X-Current-Group'] = gid;
    }
  }
  if (extraHeaders) {
    for (const [k, v] of Object.entries(extraHeaders)) {
      headers[k] = v;
      if (k === 'X-Idempotency-Key') {
        debugLog('idem', `${method} ${path} key=${v}`);
      }
    }
  }

  const response = await fetchImpl(url, {
    method,
    headers,
    credentials: 'include',
    body:
      body === undefined ? undefined : isMultipart ? (body as FormData) : JSON.stringify(body),
  });

  // 真實 status 交回 wrapper 記進診斷足跡。用 out-param 而非改 return type:成功路徑
  // 直接記 `200` 會是編出來的(可能是 201 / 204),而診斷資料不該有猜的成分。
  trace.status = response.status;

  if (response.status === 422) {
    type Parsed422 = { detail?: unknown; error?: ApiError; success?: boolean };
    let parsed: Parsed422 | null = null;
    try {
      parsed = (await response.json()) as Parsed422;
    } catch {
      /* 422 通常一定有 JSON body；解析失敗就忽略 */
    }
    // Round 50 — 三種 422 shape 共存(優先序由上而下):
    //   (a) BE envelope shape `{ success:false, error:{code:'INVALID_DATE_RANGE', ...}}`
    //       — business validation 走標準 codes,preserve BE 給的 code;normalizeEnvelopeError
    //       把 wire snake `reason_code` 補成 domain camel `reasonCode`(後端 Q5)
    //   (b) FastAPI 物件形 `{ detail: {code,message,reason_code?} }` → 升格一等 ApiError
    //       (billing 真實殼是 envelope 走 (a);此分支留作非 billing 防禦 fallback)
    //   (c) FastAPI 預設 `{ detail: [...] }` **陣列** — 沒 envelope,FE 端 fallback 用
    //       `VALIDATION_ERROR` slug(對齊既有 errorClass enum 慣例,全字串)
    // 原本 cast number `422` 進 code 讓 caller 端 `code === 'XXX'` 等 string 比對
    // 一律 miss → 走 generic fallback。改 slug + 區分 shape 後 errorMap 可命中。
    const beError = normalizeEnvelopeError(parsed?.error);
    if (beError?.code) {
      throw new ApiHttpError(422, beError);
    }
    const detailError = extractDetailObjectError(parsed);
    if (detailError) {
      throw new ApiHttpError(422, detailError);
    }
    const detail = parsed?.detail ?? null;
    throw new ApiHttpError(
      422,
      { code: 'VALIDATION_ERROR', message: 'Request validation error' },
      typeof detail === 'string' ? detail : JSON.stringify(detail),
    );
  }

  let envelope: BaseResponse<T> | null = null;
  try {
    envelope = (await response.json()) as BaseResponse<T>;
  } catch {
    envelope = null;
  }

  if (!response.ok) {
    // Round 50 — `error.code` 對齊 string slug 慣例:envelope 沒 error 時 fallback
    // 用 `HTTP_${status}` slug(對齊 enum 風格)而非 cast number 進 code,避免 errorMap
    // caller 端 `code === 'PROJECT_NOT_FOUND'` 等 string 比對誤判 + IDE infer 型別歧異。
    // 後端 Q5 — billing 等非 2xx 真實錯誤殼是 envelope `{success,error:{code,message,
    // reason_code}}`(eg. 402 PAYMENT_FAILED / 409 ORDER_NOT_CANCELLABLE + reason_code);
    // normalizeEnvelopeError 把 wire snake `reason_code` 補成 domain camel `reasonCode`。
    // detail-object handler 留作非 billing 防禦(billing envelope 不觸發)。
    // 優先序:envelope.error(normalized)> detail 物件 > generic。
    const error =
      normalizeEnvelopeError(envelope?.error) ??
      extractDetailObjectError(envelope) ??
      fallbackErrorFor(response, envelope);
    debugLog('err', `${method} ${path} status=${response.status} code=${error.code} msg=${error.message}`);
    throw new ApiHttpError(response.status, error);
  }

  if (!envelope) {
    debugLog('err', `${method} ${path} status=${response.status} (empty/non-JSON body)`);
    throw new ApiHttpError(response.status, {
      code: 'EMPTY_RESPONSE',
      message: 'Empty or non-JSON response',
    });
  }

  // R45 起 BE 新 endpoint 可能不帶 `success` field（spec §3 envelope 描述：成功路徑
  // 只 `{result: ...}` 殼）；僅當 explicit false 才視為失敗，向後相容舊 endpoint 的
  // `success: true` shape。`undefined` 視為成功（缺欄等於沒問題）。
  if (envelope.success === false) {
    // Round 50 — 同上,fallback code 改 string slug;遇到 BE 不提供 error envelope
    // 的 200 + success:false 邊角時不污染 caller 的 string 比對邏輯。
    // normalizeEnvelopeError 同步補 wire snake `reason_code` → domain camel(後端 Q5)。
    const error =
      normalizeEnvelopeError(envelope.error) ??
      { code: 'UNKNOWN_BUSINESS_ERROR', message: 'Unknown error' };
    debugLog('err', `${method} ${path} success=false code=${error.code} msg=${error.message}`);
    throw new ApiHttpError(response.status, error);
  }

  return envelope;
}

/**
 * 非 2xx 且拿不到 error envelope 時的 code —— **區分「不是應用層回的」與「BE 回的錯」**(R254)。
 *
 * Cloud Armor / GCLB 擋掉的請求**從未到達應用層**,它回的是 Google 的 HTML 錯誤頁而不是
 * 我們的 JSON envelope。兩者都可能是 403,但意義完全不同:
 *
 * | 回應主體 | 意義 | 該怎麼辦 |
 * |---|---|---|
 * | HTML / text | **不是應用層回的** —— WAF 攔截、LB 錯誤頁、上游掛掉 | 查 infra,不要去翻 BE 的業務碼 |
 * | JSON envelope | BE 自己回的業務錯誤 | 照 `error.code` 處理 |
 *
 * code 取名 `NON_JSON_RESPONSE` 而不是 `EDGE_BLOCKED`:我們**觀察得到**的事實只有
 * 「回應不是 JSON envelope」,至於是 WAF 擋、LB 503 還是上游錯誤頁,前端分不出來 ——
 * 名字不該宣稱它不知道的原因。
 *
 * 這個區分實測救過兩輪:封面上傳 403(R246.3)與問題回報附件 403(R252)都是 WAF,
 * 但當時前端一律看到含糊的 `HTTP_403`,得靠人去 curl 才分得出來。BE 也在 docs/94 §7
 * 把它列成驗收項。
 *
 * ⚠️ 判準是「**envelope 解析不出來** + content-type 不是 JSON」而不是只看 content-type:
 * 有些 BE 錯誤回應沒帶 content-type(測試 mock 也常省略),那些的 envelope 是解析得出來的,
 * 不該被誤判成邊緣擋。
 */
function fallbackErrorFor(response: Response, envelope: unknown): ApiError {
  if (envelope === null) {
    const contentType = response.headers?.get?.('content-type') ?? '';
    if (contentType && !contentType.includes('application/json')) {
      return {
        code: 'NON_JSON_RESPONSE',
        message: `Response was ${contentType}, not our JSON envelope — the request likely never reached the application (edge/WAF block or upstream error page)`,
      };
    }
  }
  return { code: `HTTP_${response.status}`, message: response.statusText || 'HTTP error' };
}

/**
 * 對外的 request 入口 — 在 {@link httpRequestCore} 外面包一層診斷記錄(R249)。
 *
 * 這裡是**全站唯一**的 HTTP 出口(所有 `http*Service` 都經過),所以「使用者剛才做了哪些
 * API 呼叫」只要插這一點就零遺漏、零散落。記錄內容嚴格限形狀不含內容(method /
 * 正規化 path / status / error code / 耗時 / clientRequestId),判準與理由見
 * [diagnosticsBuffer](../../lib/diagnostics/diagnosticsBuffer.ts)。
 *
 * ⚠️ **記錄本身絕不可以讓請求失敗**:buffer 寫入包 try/catch,診斷是附加價值,
 * 不是請求的前置條件。
 */
export async function httpRequest<T>(
  config: HttpClientConfig,
  method: string,
  path: string,
  body?: unknown,
  extraHeaders?: Record<string, string>,
): Promise<BaseResponse<T>> {
  const clientRequestId = newClientRequestId();
  const startedAt = Date.now();
  const trace: { status: number | null } = { status: null };
  try {
    const result = await httpRequestCore<T>(
      config,
      method,
      path,
      body,
      extraHeaders,
      clientRequestId,
      trace,
    );
    safeRecord(method, path, trace.status, null, startedAt, clientRequestId);
    return result;
  } catch (e) {
    safeRecord(method, path, trace.status, errorCodeOf(e), startedAt, clientRequestId);
    throw e;
  }
}

/**
 * 失敗原因收斂成一個 code。
 *
 * `ApiHttpError` 已經帶 BE 的 code(含 transport 自己補的 `HTTP_502` /
 * `VALIDATION_ERROR` slug)。非 ApiHttpError 表示**連回應都沒拿到** —— 網路中斷、
 * DNS、被 WAF 在 TLS 之後掐斷、`AbortError` 逾時。這一類冠 `FETCH_` 前綴:後端看到
 * `FETCH_TypeError` 配 `status: null` 就知道請求沒進到應用層,不必再翻自己的 log。
 */
function errorCodeOf(e: unknown): string {
  // ⚠️ `ApiError.code` 是 `string | number` —— BE 的 429 回的是**數字** 429
  // (docs/87 B-3 已確認),不是 slug。診斷欄位統一成字串,否則同一種失敗在
  // 後端看到兩種型別。
  if (e instanceof ApiHttpError) return String(e.error?.code ?? 'UNKNOWN_ERROR');
  if (e instanceof Error) return `FETCH_${e.name}`;
  return 'FETCH_ERROR';
}

function safeRecord(
  method: string,
  path: string,
  status: number | null,
  errorCode: string | null,
  startedAt: number,
  clientRequestId: string,
): void {
  try {
    recordApiCall({
      method,
      path,
      status,
      errorCode,
      durationMs: Date.now() - startedAt,
      clientRequestId,
    });
  } catch {
    // 診斷記錄失敗不可以影響請求結果 — 安靜吞掉。
  }
}

/**
 * :func:`httpRequest` 的 401 統一處理版本。
 *
 * **R45 起無 refresh chain**（spec §1.4 — `POST /api/front_auth/refresh` 410 Gone）：
 * 401 + ``error.code === 'AUTH_INVALID_TOKEN'`` → 直接呼叫 ``config.onAuthLost?.()``
 * 通知應用層（清 store → RequireAuth 觀察 ``user === null`` 自動
 * ``<Navigate to="/login">``）+ 拋出原 401 error。
 *
 * 為什麼還保留分支（不直接用 :func:`httpRequest`）：統一 entry，避免各 service 端
 * 各自實作「401 通知 app 層」邏輯。`signIn` / `getGoogleConfig` 等 auth-entry 端點
 * 仍直接用 :func:`httpRequest`，避免 boot 階段未登入時誤觸 onAuthLost。
 */
export async function httpRequestWithRefresh<T>(
  config: HttpClientConfig,
  method: string,
  path: string,
  body?: unknown,
  options: InternalRequestOptions = {},
): Promise<BaseResponse<T>> {
  try {
    return await httpRequest<T>(config, method, path, body, options.extraHeaders);
  } catch (e) {
    if (
      e instanceof ApiHttpError &&
      e.status === 401 &&
      e.error?.code === 'AUTH_INVALID_TOKEN'
    ) {
      try {
        config.onAuthLost?.();
      } catch (cbErr) {
        // onAuthLost 不該丟錯；防呆 log 但不擋我們把原 401 propagate
        console.error('[http] onAuthLost handler threw', cbErr);
      }
    }
    throw e;
  }
}

